import { Link } from 'react-router-dom';
import { TOKO_APPS } from '@/config/apps';
import styles from './Home.module.css';

export function Home() {
  const addApp = TOKO_APPS.find(app => app.id === 'addition');
  const multApp = TOKO_APPS.find(app => app.id === 'multiplication');

  const scrollToApps = () => {
    document.getElementById('apps-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Make Math Practice Fun & Effective</h1>
          <p className={styles.heroSubtitle}>
            Build lightning-fast math skills with timed flashcard drills and multiplayer races.
            Perfect for kids ages 3-12—and adults looking to refresh their math skills!
          </p>
          <div className={styles.heroCtas}>
            <Link to="/race" className={styles.heroPrimaryButton}>
              Start Racing
            </Link>
            <button onClick={scrollToApps} className={styles.heroSecondaryButton}>
              Explore Apps
            </button>
          </div>
        </div>
      </section>

      {/* Apps Section */}
      <section id="apps-section" className={styles.appsSection}>
        <div className={styles.appsSectionInner}>
          <h2 className={styles.sectionTitle}>Our Apps</h2>
          <p className={styles.sectionSubtitle}>
            Master math facts with our timed flashcard apps designed for young learners
          </p>
          <div className={styles.productCards}>
            {addApp && (
              <Link to={`/app/${addApp.id}`} className={`${styles.productCard} ${styles.productCardAdd}`}>
                <img src={addApp.icon} alt={addApp.name} className={styles.productCardIcon} />
                <h3 className={styles.productCardTitle}>{addApp.name}</h3>
                <p className={styles.productCardSubtitle}>Addition Flashcards for Ages 3-12</p>
                <ul className={styles.productCardFeatures}>
                  <li>Schedule A (22 units) & Schedule B (53 units)</li>
                  <li>Timed practice challenges</li>
                  <li>Detailed progress tracking</li>
                </ul>
              </Link>
            )}
            {multApp && (
              <Link to={`/app/${multApp.id}`} className={`${styles.productCard} ${styles.productCardMult}`}>
                <img src={multApp.icon} alt={multApp.name} className={styles.productCardIcon} />
                <h3 className={styles.productCardTitle}>{multApp.name}</h3>
                <p className={styles.productCardSubtitle}>Multiplication Flashcards for Ages 3-12</p>
                <ul className={styles.productCardFeatures}>
                  <li>Schedule A (22 units) & Schedule B (53 units)</li>
                  <li>Timed practice challenges</li>
                  <li>Detailed progress tracking</li>
                </ul>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <h2 className={styles.sectionTitle}>Key Features</h2>
        <p className={styles.sectionSubtitle}>
          Everything you need to build real math fluency
        </p>
        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#9201;</span>
            <h3 className={styles.featureTitle}>Timed Flashcards</h3>
            <p className={styles.featureDescription}>
              Practice under 3, 5, or 10-second time limits to match your skill level
            </p>
          </div>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#127942;</span>
            <h3 className={styles.featureTitle}>Multiplayer Racing</h3>
            <p className={styles.featureDescription}>
              Host live races with 4-letter join codes and compete on real-time leaderboards
            </p>
          </div>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#128218;</span>
            <h3 className={styles.featureTitle}>Two Learning Paths</h3>
            <p className={styles.featureDescription}>
              Schedule A (22 units) for quick learners, Schedule B (53 units) for gradual mastery
            </p>
          </div>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#9889;</span>
            <h3 className={styles.featureTitle}>Instant Feedback</h3>
            <p className={styles.featureDescription}>
              See immediately if answers are correct, slow, or missed
            </p>
          </div>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#128202;</span>
            <h3 className={styles.featureTitle}>Detailed Analytics</h3>
            <p className={styles.featureDescription}>
              Color-coded progress grids show exactly which facts need more practice
            </p>
          </div>
          <div className={styles.featureCard}>
            <span className={styles.featureIcon}>&#128118;</span>
            <h3 className={styles.featureTitle}>Clean Interface</h3>
            <p className={styles.featureDescription}>
              Large buttons and clear visuals designed specifically for young learners
            </p>
          </div>
        </div>
      </section>

      {/* Race Spotlight Section */}
      <section className={styles.raceSpotlight}>
        <div className={styles.raceSpotlightInner}>
          <div className={styles.raceSpotlightContent}>
            <h2>Compete in Live Math Races</h2>
            <p>
              Turn math practice into an exciting competition! Host races where students
              compete in real-time to answer flashcards fastest and most accurately.
            </p>
            <ul className={styles.raceFeatureList}>
              <li>
                <span className={styles.raceFeatureCheck}>&#10003;</span>
                Simple 4-letter join codes for easy access
              </li>
              <li>
                <span className={styles.raceFeatureCheck}>&#10003;</span>
                Live leaderboard updates during races
              </li>
              <li>
                <span className={styles.raceFeatureCheck}>&#10003;</span>
                Exciting countdown timer to start
              </li>
              <li>
                <span className={styles.raceFeatureCheck}>&#10003;</span>
                Podium results with 1st, 2nd, 3rd places
              </li>
            </ul>
            <Link to="/race" className={styles.raceSpotlightButton}>
              Host a Race
            </Link>
          </div>
          <div className={styles.racePreview}>
            <div className={styles.raceScreenshotPlaceholder}>
              {/* Replace this div with an actual screenshot:
                  <img src="/screenshots/race-preview.png" alt="Race interface preview" /> */}
              <div className={styles.placeholderJoinCode}>ABCD</div>
              <p className={styles.placeholderText}>Enter the code to join</p>
            </div>
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section className={styles.audienceSection}>
        <h2 className={styles.sectionTitle}>Who It's For</h2>
        <p className={styles.sectionSubtitle}>
          TOKO Math Club works for learners and educators alike
        </p>
        <div className={styles.audienceCards}>
          <div className={`${styles.audienceCard} ${styles.audienceCardFamilies}`}>
            <span className={styles.audienceIcon}>&#127968;</span>
            <h3 className={styles.audienceTitle}>For Families</h3>
            <p className={styles.audienceDescription}>
              Build a daily math practice habit for kids ages 3-12. Track progress and watch
              confidence grow with structured learning paths.
            </p>
          </div>
          <div className={`${styles.audienceCard} ${styles.audienceCardTeachers}`}>
            <span className={styles.audienceIcon}>&#127979;</span>
            <h3 className={styles.audienceTitle}>For Teachers</h3>
            <p className={styles.audienceDescription}>
              Use as a supplemental practice tool. Host classroom races to make math
              review engaging and competitive.
            </p>
          </div>
          <div className={`${styles.audienceCard} ${styles.audienceCardSchools}`}>
            <span className={styles.audienceIcon}>&#127891;</span>
            <h3 className={styles.audienceTitle}>For Schools</h3>
            <p className={styles.audienceDescription}>
              Run math club competitions and track student progress across grade levels
              with structured curriculum alignment.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaSectionInner}>
          <h2 className={styles.ctaTitle}>Ready to Make Math Fun?</h2>
          <p className={styles.ctaSubtitle}>
            Start racing today or download our apps for on-the-go practice
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/race" className={styles.ctaPrimaryButton}>
              Host a Race
            </Link>
            <button onClick={scrollToApps} className={styles.ctaSecondaryButton}>
              View Apps
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
